import * as IoC from 'typescript-ioc';
import * as mobx from 'mobx';
import { addDays, format } from 'date-fns';
import { IntlShape } from 'react-intl';
import {
    utcToZonedTime,
    getFormattedTimezoneOffsetByTimezone
} from '../../../../../../infrastructure/utils/date-fns-overrides.utils';
import { IReactIntlProvider } from '../../../../../../infrastructure/react-intl-provider';
import { EXPIRY_DATE_INFO_TEXT, EXPIRY_DAYS_RANGE_TEXT, EXPIRY_DAYS_REQUIRED_TEXT } from './meeting-expiry-date.texts';

class MeetingExpiryDateVM {
    @mobx.observable
    private isEnabled: boolean = false;

    @mobx.observable
    private days: number | null = null;

    @mobx.observable
    private meetingDate: Date | null = null;

    @mobx.observable
    private startDate: Date | null = null;

    @mobx.observable
    private timezone: string = '';

    constructor(
        initialExpiryDays: number | null,
        @IoC.Inject private readonly intlProvider: IReactIntlProvider = IoC.Container.get(IReactIntlProvider)
    ) {
        this.init(initialExpiryDays);
    }

    private get intl(): IntlShape {
        return this.intlProvider.getIntl();
    }

    public get enabled(): boolean {
        return this.isEnabled;
    }

    public get daysValue(): number | null {
        return this.days;
    }

    @mobx.computed
    public get expiryDays(): number | null {
        return this.isEnabled && this.days ? this.days : null;
    }

    @mobx.computed
    public get calculatedExpiryDate(): Date | null {
        if (!this.isEnabled || !this.days || !this.meetingDate) {
            return null;
        }

        const expiryDate = addDays(this.meetingDate, this.days);
        return expiryDate;
    }

    @mobx.computed
    public get displayText(): string {
        if (!this.calculatedExpiryDate || !this.isEnabled) {
            return '';
        }

        const zonedDate = utcToZonedTime(this.calculatedExpiryDate, this.timezone);
        const formattedDate = format(zonedDate, 'do MMM yyyy');
        const timezoneOffset = getFormattedTimezoneOffsetByTimezone(this.timezone);

        return this.intl.formatMessage(
            EXPIRY_DATE_INFO_TEXT,
            {
                timezone: timezoneOffset,
                date: formattedDate
            }
        );
    }

    public getValidationError(): string | null {
        if (!this.isEnabled) {
            return null;
        }

        if (!this.days) {
            return this.intl.formatMessage(EXPIRY_DAYS_REQUIRED_TEXT);
        }

        const days = Number(this.days);
        if (isNaN(days) || !Number.isInteger(days) || days < 1 || days > 999) {
            return this.intl.formatMessage(EXPIRY_DAYS_RANGE_TEXT);
        }

        return null;
    }

    @mobx.action
    public setMeetingDate = (date: Date | null): void => {
        this.meetingDate = date;
    };

    @mobx.action
    public setStartDate = (date: Date | null): void => {
        this.startDate = date;
    };

    @mobx.action
    public setTimezone = (timezone: string): void => {
        this.timezone = timezone;
    };

    @mobx.action
    public setDays = (value: string): void => {
        this.days = value === '' ? null : Number(value);
    };

    @mobx.action
    public toggleEnabled = (): void => {
        this.isEnabled = !this.isEnabled;

        if (this.isEnabled && !this.days) {
            this.days = 30;
        }

        if (!this.isEnabled) {
            this.days = null;
        }
    };

    public hasValidationError(): boolean {
        return !!this.getValidationError();
    }

    private init = (initialExpiryDays: number | null): void => {
        if (initialExpiryDays && initialExpiryDays > 0) {
            this.isEnabled = true;
            this.days = initialExpiryDays;
        } else {
            this.isEnabled = false;
            this.days = 30;
        }
    };
}

export { MeetingExpiryDateVM };
